import AddIcon from '@mui/icons-material/Add'
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined'
import PlaylistAddCircleOutlinedIcon from '@mui/icons-material/PlaylistAddCircleOutlined'
import {
  Box,
  Button,
  Divider,
  Skeleton,
  Stack,
  Typography
} from '@mui/material'
import {
  CompanyUsersStatus,
  CompanyUserWithCompanyAndRole
} from '@shared/types/companies.types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import EmptyCards from '../../assets/images/EmptyCards.svg'
import CompanyPaper from '../../components/CompanyPaper'
import EmptySearchResults from '../../components/EmptySearchResults'
import SearchInput from '../../components/forms/SearchInput'
import { useAuthStore } from '../../hooks/useAuthStore'
import { useUserCompanies } from '../../hooks/useQueries/useCompanies'

const paperContainerStyles = {
  flex: '0 0 calc(33.333% - 1rem)',
  minWidth: '22rem',
  marginBottom: '2rem'
}

const flexContainerStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  rowGap: '0rem',
  columnGap: '1rem',
  maxWidth: '100%',
  overflowY: 'auto',
  height: '31.5625rem',
  alignContent: 'flex-start'
}

export default function Companies() {
  const { t } = useTranslation()
  const { userId } = useAuthStore()
  const [searchText, setSearchText] = useState('')
  const [filteredCompanies, setFilteredCompanies] = useState<
    CompanyUserWithCompanyAndRole[]
  >([])

  const { data: companies, isLoading } = useUserCompanies(
    userId!,
    CompanyUsersStatus.ACTIVE
  )

  const handleFilter = (searchValue: string) => {
    let filtered = [...(companies || [])]

    if (searchValue) {
      filtered = filtered.filter(company =>
        JSON.stringify(company)
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      )
    }

    setFilteredCompanies(filtered)
  }

  useEffect(() => {
    if (companies && companies.length > 0) {
      setFilteredCompanies(companies)
    }
  }, [companies])

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
          width: '100%',
          height: '47.625rem',
          borderRadius: '1rem',
          padding: ' 1.5rem 3rem 2rem 3rem',
          border: '1px solid #00897B',
          background:
            'linear-gradient(156deg, #1B5A4B -21.63%, #171B1E 42.81%), #171B1E',
          boxShadow:
            '0 4px 18px 3px rgba(0, 0, 0, 0.12), 0 10px 14px 1px rgba(0, 0, 0, 0.14), 0 6px 6px -3px rgba(0, 0, 0, 0.20)'
        }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          padding='1rem 1.25rem'
        >
          <Skeleton
            variant='rectangular'
            width='8rem'
            height='2.5rem'
            sx={{ borderRadius: '.5rem', bgcolor: 'rgba(255, 255, 255, 0.1)' }}
          />
          <Stack direction='row' spacing='1rem'>
            <Skeleton
              variant='rectangular'
              width='12rem'
              height='2.5rem'
              sx={{
                borderRadius: '.5rem',
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }}
            />
            <Skeleton
              variant='rectangular'
              width='4rem'
              height='2.5rem'
              sx={{
                borderRadius: '.5rem',
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }}
            />
          </Stack>
        </Stack>

        <Box sx={flexContainerStyles}>
          {[1, 2, 3, 4, 5].map(index => (
            <Box key={index} sx={paperContainerStyles}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '22rem',
                  borderRadius: '1rem',
                  backgroundColor: '#2A2D30',
                  gap: '.5rem',
                  padding: '1rem'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
                  >
                    <Skeleton
                      variant='rectangular'
                      width='3.5rem'
                      height='3.5rem'
                      sx={{
                        borderRadius: '0.25rem',
                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                      }}
                    />
                    <Box>
                      <Skeleton
                        variant='text'
                        width='8rem'
                        height='1.25rem'
                        sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
                      />
                      <Box
                        sx={{ display: 'flex', gap: '0.62rem', mt: '0.25rem' }}
                      >
                        <Skeleton
                          variant='rectangular'
                          width='4rem'
                          height='1.5rem'
                          sx={{
                            borderRadius: '1rem',
                            bgcolor: 'rgba(255, 255, 255, 0.1)'
                          }}
                        />
                        <Skeleton
                          variant='rectangular'
                          width='3rem'
                          height='1.5rem'
                          sx={{
                            borderRadius: '1rem',
                            bgcolor: 'rgba(255, 255, 255, 0.1)'
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Skeleton
                    variant='circular'
                    width='2rem'
                    height='2rem'
                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
                  />
                </Box>

                <Skeleton
                  variant='rectangular'
                  width='100%'
                  height='2.5rem'
                  sx={{
                    borderRadius: '0.5rem',
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>

        <Skeleton
          variant='rectangular'
          width='100%'
          height='0.125rem'
          sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
        />

        <Skeleton
          variant='rectangular'
          width='100%'
          height='2.5rem'
          sx={{ borderRadius: '0.5rem', bgcolor: 'rgba(255, 255, 255, 0.1)' }}
        />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
        width: '100%',
        height: '47.625rem',
        borderRadius: '1rem',
        padding: ' 1.5rem 3rem 2rem 3rem',
        border: '1px solid #00897B',
        background:
          'linear-gradient(156deg, #1B5A4B -21.63%, #171B1E 42.81%), #171B1E',
        boxShadow:
          '0 4px 18px 3px rgba(0, 0, 0, 0.12), 0 10px 14px 1px rgba(0, 0, 0, 0.14), 0 6px 6px -3px rgba(0, 0, 0, 0.20)'
      }}
    >
      <Stack
        direction='row'
        justifyContent='space-between'
        padding='1rem 1.25rem'
      >
        <Typography variant='h5'>{t('companies.title')}</Typography>

        <Stack direction='row' spacing='1rem'>
          <SearchInput
            searchText={searchText}
            setSearchText={setSearchText}
            handleFilter={handleFilter}
          />
          <Button
            variant='text'
            color='primary'
            disabled={!searchText}
            onClick={() => handleFilter(searchText)}
            sx={{
              textTransform: 'none',
              padding: '0.375rem 0.5rem'
            }}
          >
            {t('common.search')}
          </Button>
        </Stack>
      </Stack>

      {companies?.length ? (
        filteredCompanies?.length ? (
          <>
            <Box sx={flexContainerStyles}>
              {filteredCompanies.map(company => (
                <Box key={company.company.id} sx={paperContainerStyles}>
                  <CompanyPaper company={company} />
                </Box>
              ))}
            </Box>

            <Divider sx={{ height: '.125rem', color: 'primary.light' }} />

            <Button
              component={Link}
              to='/companies/create'
              variant='text'
              size='large'
              color='primary'
              startIcon={<PlaylistAddCircleOutlinedIcon />}
              sx={{ padding: ' 0.5rem 0.6875rem', textTransform: 'none' }}
            >
              {t('companies.create_new_button')}
            </Button>
          </>
        ) : (
          <EmptySearchResults />
        )
      ) : (
        <Stack
          direction='column'
          spacing='2rem'
          padding='3.25rem 4rem'
          alignItems='flex-start'
        >
          <img
            src={EmptyCards}
            alt='Empty List'
            style={{
              width: '9.25rem',
              height: '9.25rem',
              objectFit: 'contain'
            }}
          />
          <Typography variant='h6'>
            {t('companies.empty.create.text')}
          </Typography>
          <Button
            component={Link}
            to='/companies/create'
            color='primary'
            variant='contained'
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              padding: '.5rem 1.375rem',
              width: '17.1875rem'
            }}
          >
            {t('companies.empty.create.button')}
          </Button>
          <Typography variant='h6'>
            {t('companies.empty.directory.text')}
          </Typography>
          <Button
            component={Link}
            to='/companies/directory'
            variant='outlined'
            color='primary'
            startIcon={<ExploreOutlinedIcon />}
            sx={{
              textTransform: 'none',
              padding: '0.375rem 1rem',
              background: 'rgba(178, 223, 219, 0.1)',
              width: '17.1875rem'
            }}
          >
            {t('companies.empty.directory.button')}
          </Button>
        </Stack>
      )}
    </Box>
  )
}
